import React from 'react'
import { useQuery } from '@apollo/react-hooks'
import moment from 'moment'
import { ChevronsRight } from 'lucide-react'
import { first } from 'lodash'
import { JOB_LOG_LIST } from '../../constants/graphql/query'

const TOPICS = {
  startDestination: 'ต้นทาง',
  endDestination: 'ปลายทาง',
  customer: 'ผู้จ้าง',
}

// Function to parse log message and extract change information
const parseLogMessage = details => details.map(change => ({
    topic: TOPICS[change.fieldName],
    originalData: change.oldValue || '-',
    changedData: change.newValue || '-',
  }))

const HistoryRow = ({ item }) => (
  <div className="grid grid-cols-7 gap-4 px-4 py-4 border-b border-gray-200 bg-white my-2 rounded-sm">
    {/* Date/Time */}
    <div className="text-base font-semibold">
      <p className="text-black">{moment(item.date).format('DD/MM/YYYY')}</p>
      <p className="text-sm font-medium">{moment(item.date).format('HH:mm')}</p>
    </div>
    {/* Modifier */}
    <div className="text-black text-base border-r border-gray-300 pr-4">
      {item.modifier}
    </div>
    {/* Topic */}
    <div className='col-span-5'>
      {item.changes.map((change, idx) => (
        <div key={idx} className='grid grid-cols-5 gap-4'>
          <div
            key={idx}
            className={`font-medium text-gray-500 text-base font-semibold ${idx < item.changes.length - 1 ? 'mb-1' : ''}`}
          >
            {change.topic}
          </div>
          <div
            key={idx}
            className={`text-black text-base ${idx < item.changes.length - 1 ? 'mb-1' : ''} flex justify-between w-full col-span-2`}
          >
            {change.originalData}
            <ChevronsRight className="w-4 h-4 text-gray-400 inline flex-shrink-0 mt-1" />
          </div>
          <div
            key={idx}
            className={`text-black text-base font-medium ${idx < item.changes.length - 1 ? 'mb-1' : ''} col-span-2`}
          >
            {change.changedData}
          </div>
        </div>
      ))}
    </div>
  </div>
)

const HistoryHeader = () => (
  <div className="grid grid-cols-7 gap-4 px-4 py-3 font-bold text-gray-700">
    <div className="text-base text-gray-500 font-semibold">วันที่และเวลาที่แก้ไข</div>
    <div className="text-base text-gray-500 font-semibold">ผู้แก้ไข</div>
    <div className="text-base text-gray-500 font-semibold">หัวข้อที่แก้ไข</div>
    <div className="text-base text-gray-500 font-semibold col-span-2">ข้อมูลเดิม</div>
    <div className="text-base text-gray-500 font-semibold col-span-2">ข้อมูลที่เปลี่ยน</div>
  </div>
)

const Loading = () => (
  <div className="flex justify-center items-center h-48 text-base text-gray-600 font-inter">
    กำลังโหลด...
  </div>
)

const Error = ({ message }) => (
  <div className="flex justify-center items-center h-48 text-base text-red-500 font-inter">
    เกิดข้อผิดพลาด: {message || 'ไม่สามารถโหลดข้อมูลได้'}
  </div>
)

const JobHistory = ({ props }) => {
  const jobId = props?.match?.params?.id

  const {
    data: historyData,
    loading: historyLoading,
    error: historyError,
  } = useQuery(JOB_LOG_LIST, {
    variables: {
      jobId: parseInt(jobId, 10),
    },
    skip: !jobId,
  })

  if (historyLoading) return <Loading />
  if (historyError) return <Error message={historyError?.message} />

  const jobNumber = first(historyData?.JobLogList)?.Job?.jobCode

  const historyLogs = historyData?.JobLogList || []

  const processedHistoryData = historyLogs.length > 0
    ? historyLogs.map(log => ({
        id: log.id,
        date: log.createdAt,
        modifier: `${log.Editor?.firstname || ''} ${log.Editor?.lastname || ''}`.trim(),
        changes: parseLogMessage(log.Details),
      }))
    : []

  return (
    <div className="flex flex-col font-inter min-h-screen">
      {/* Header Section */}
      <div className="mb-4">
        <p className="text-2xl font-semibold text-primary font-inter">
          ประวัติการแก้ไข สัญญางาน {jobNumber}
        </p>
      </div>
      {/* History List */}
      <div className="pb-10">
        <HistoryHeader />
        {processedHistoryData.map(item => (
          <HistoryRow key={item.id} item={item} />
        ))}
      </div>
    </div>
  )
}

export default JobHistory
